/** Isolated wording only. This client cannot prepare, approve or send email. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/mail-connections/icloud/write`;
// Do not use global diagnostics, conversation or offline-cache interceptors.
const client = axios.create({ withCredentials: true, timeout: 35000,
  headers: { "X-NDG-Mail-Review": "browser-v1", "Cache-Control": "no-store" } });
const fieldStyle = { width: "100%", background: "transparent", color: "inherit", border: "1px solid #64748b", borderRadius: ".35rem", padding: ".7rem", font: "inherit" };

export default function MailDraftAssistant(props) {
  const { initialInstruction = "", initialRequestId = "", connectionVersion, busy } = props;
  const [instruction, setInstruction] = useState(initialInstruction);
  const [notice, setNotice] = useState("");
  const [generating, setGenerating] = useState(false);
  const current = useRef(props);
  current.current = props;
  const live = useRef(false), epoch = useRef(0), pending = useRef(null), localSeen = useRef(null);
  const seen = props.initialSeen || localSeen;

  useEffect(() => {
    live.current = true;
    return () => { live.current = false; epoch.current += 1; pending.current?.abort(); };
  }, []);

  async function generate(words) {
    const source = current.current;
    if (!words.trim() || source.busy || pending.current || !source.connectionVersion) return;
    const controller = new AbortController();
    pending.current = controller;
    const version = source.connectionVersion, token = ++epoch.current;
    const requestId = window.crypto.randomUUID();
    setNotice(""); setGenerating(true); source.onBusy(true);
    try {
      const { data } = await client.post(`${BASE}/draft`, {
        request_id: requestId, connection_version: version,
        instruction: words, subject: source.subject, text: source.text,
      }, { signal: controller.signal });
      if (!live.current || token !== epoch.current || current.current.connectionVersion !== version) return;
      if (data.connection_version !== version || data.request_id !== requestId || data.sent !== false
          || typeof data.subject !== "string" || typeof data.text !== "string" || typeof data.question !== "string"
          || data.subject.length > 300 || data.text.length > 50000 || data.question.length > 500
          || /[\r\n\x00]/.test(data.subject)) throw new Error("Invalid draft response");
      if (data.question) {
        if (data.subject || data.text) throw new Error("Ambiguous response");
        setNotice(data.question);
      } else {
        if (!data.subject.trim() || !data.text.trim()) throw new Error("Empty draft response");
        current.current.onDraft({ subject: data.subject, text: data.text });
        setNotice("Draft ready. Check the wording and recipient. Nothing has been sent.");
        setInstruction("");
      }
    } catch (error) {
      if (live.current && token === epoch.current && !controller.signal.aborted) {
        setNotice(error?.response?.status === 409
          ? "Your mailbox connection changed. Reopen the editor; your draft was not replaced."
          : "Karen could not finish the wording. Your current draft is unchanged. Nothing was sent.");
      }
    } finally {
      if (live.current && token === epoch.current) {
        pending.current = null; setGenerating(false); current.current.onBusy(false);
      }
    }
  }

  useEffect(() => {
    const key = `${initialRequestId}:${initialInstruction}`;
    if (!initialInstruction.trim() || !connectionVersion || busy || seen.current === key) return;
    seen.current = key;
    setInstruction(initialInstruction);
    generate(initialInstruction);
    // Deliberately react only to an explicit new instruction, not draft edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInstruction, initialRequestId, connectionVersion, busy]);

  return <div data-testid="karen-email-drafting" style={{ display: "grid", gap: ".6rem" }}>
    <label>Tell Karen what to write or change
      <textarea aria-label="Tell Karen what to write or change" rows={3} maxLength={6000}
        style={fieldStyle} value={instruction} disabled={busy} autoComplete="off"
        placeholder="For example: explain that I am still waiting for my Part 3 test date."
        onChange={(event) => setInstruction(event.target.value)} />
    </label>
    <button type="button" className="karen-panel-button" disabled={busy || !instruction.trim()}
      onClick={() => generate(instruction)}>{generating ? "Karen is drafting…" : props.text ? "Update wording with Karen" : "Write it with Karen"}</button>
    {notice && <p role="status">{notice}</p>}
    <p style={{ fontSize: ".95rem" }}>Karen uses your instruction and the current outgoing draft to help with wording. The original email is not sent to the writing model. Drafting does not enable sending.</p>
  </div>;
}
